'use client';

import { Grid2 as Grid } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { AbgTypography, TextFormControl, useTranslation } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useCountriesList } from '../../../../hooks';
import { CITY_LABELED_AS_SUBURB_COUNTRIES } from '../../../../shared/constants/registration-form.constants';
import { decodeHtmlEntities } from '../../../../shared/utils';

import {
  ADDRESS_LINE_1_MAX_LENGTH,
  ADDRESS_LINE_1_MIN_LENGTH,
  CITY_REGEX,
  FIELD_REGEX,
  POSTAL_CODE_MAX_LENGTH,
  POSTAL_CODE_MIN_LENGTH,
  POSTCODE_REGEX,
} from './contact-information-emea.constants';
import { titleStyling } from './contact-information-emea.styles';
import { ContactInformationProperties } from './contact-information-emea.types';

export const ContactInformationEmea = ({
  model,
  title,
  prefix = '',
  testIdPrefix = 'contact-information',
  additionalOptions,
  pageType,
  i18n,
  hideCompany = false,
  disableCountry = false,
}: ContactInformationProperties) => {
  const fieldNames = additionalOptions?.fieldNames;
  const translation = model ? model.i18n : i18n;
  const t = useTranslation(translation ?? {});

  const pageConfig = usePageContext();
  const countries = useCountriesList(`?locale=${pageConfig?.locale}`);

  const selectedCountry = useWatch({
    name: `${prefix}country`,
    defaultValue: pageConfig.country?.toUpperCase() ?? '',
  }) as string;
  const isCityLabeledAsSuburb = CITY_LABELED_AS_SUBURB_COUNTRIES.includes(
    selectedCountry,
  );

  const countriesData = useMemo(() => {
    const countriesMap = new Map(
      countries.map((country) => [country.code, country]),
    );

    return (
      pageConfig.supportedCountries
        ?.map((country) => ({
          value: country,
          label: countriesMap.get(country)?.name ?? country,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [countries, pageConfig]);

  return (
    <Grid container rowSpacing={{ xs: 2.5, sm: 3 }} columnSpacing={2}>
      {title && (
        <AbgTypography
          variant="h6"
          sx={titleStyling}
          data-testid="registration-form-contact-information-title"
        >
          {title}
        </AbgTypography>
      )}

      {!hideCompany && (
        <Grid size={12}>
          <TextFormControl
            name={`${prefix}company`}
            label={t('reservationCheckout.billingDetailsForm.company.label')}
            placeholder={t('global.field.company.placeholder')}
            testIdPrefix={testIdPrefix}
            testId="company"
            textFieldProps={{
              slotProps: {
                htmlInput: { maxLength: 30 },
              },
              fullWidth: true,
            }}
            validationRules={{
              pattern: {
                value: FIELD_REGEX,
                message: t(
                  'registrationFormEmea.validation.invalidCompany.label',
                ),
              },
            }}
          />
        </Grid>
      )}

      <Grid
        size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 12}
        sx={hideCompany ? { mt: 3 } : {}}
      >
        <TextFormControl
          name={`${prefix}addressLine1`}
          label={t('global.field.addressLine1.label')}
          placeholder={t('global.field.addressLine1.placeholder')}
          testIdPrefix={testIdPrefix}
          testId="address-line-1"
          textFieldProps={{
            slotProps: {
              htmlInput: { maxLength: 30 },
            },
            fullWidth: true,
          }}
          validationRules={{
            pattern: {
              value: FIELD_REGEX,
              message: t(
                'registrationFormEmea.validation.invalidAddressLine1.label',
              ),
            },
            required: t('global.field.validation.fieldRequired.message', {
              field: t('global.field.addressLine1.label'),
            }),
            minLength: {
              value: ADDRESS_LINE_1_MIN_LENGTH,
              message: t('global.field.validation.invalidLength.message', {
                field: t('global.field.addressLine1.label'),
                minLength: ADDRESS_LINE_1_MIN_LENGTH,
                maxLength: ADDRESS_LINE_1_MAX_LENGTH,
              }),
            },
          }}
          required
        />
      </Grid>

      <Grid size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 12}>
        <TextFormControl
          name={`${prefix}addressLine2`}
          label={t('global.field.addressLine2.label')}
          placeholder={t('global.field.addressLine2.placeholder')}
          testIdPrefix={testIdPrefix}
          testId="address-line-2"
          textFieldProps={{
            slotProps: {
              htmlInput: { maxLength: 30 },
            },
            fullWidth: true,
          }}
          validationRules={{
            pattern: {
              value: FIELD_REGEX,
              message: t(
                'registrationFormEmea.validation.invalidAddressLine2.label',
              ),
            },
          }}
        />
      </Grid>

      <Grid size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 12}>
        <TextFormControl
          name={`${prefix}addressLine3`}
          label={t('global.field.addressLine3.label')}
          placeholder={t('global.field.addressLine3.placeholder')}
          testIdPrefix={testIdPrefix}
          testId="address-line-3"
          textFieldProps={{
            slotProps: {
              htmlInput: { maxLength: 30 },
            },
            fullWidth: true,
          }}
          validationRules={{
            pattern: {
              value: FIELD_REGEX,
              message: t(
                'registrationFormEmea.validation.invalidAddressLine3.label',
              ),
            },
          }}
        />
      </Grid>

      <Grid size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 12}>
        <TextFormControl
          name={`${prefix}city`}
          label={
            isCityLabeledAsSuburb
              ? t('global.field.suburb.label')
              : t('global.field.city.label')
          }
          placeholder={
            isCityLabeledAsSuburb
              ? t('global.field.suburb.placeholder')
              : t('global.field.city.placeholder')
          }
          testIdPrefix={testIdPrefix}
          testId="city"
          textFieldProps={{
            slotProps: {
              htmlInput: { maxLength: 29 },
            },
            fullWidth: true,
          }}
          validationRules={{
            pattern: {
              value: CITY_REGEX,
              message: t('registrationFormEmea.validation.invalidCity.label'),
            },
            required: t('global.field.validation.fieldRequired.message', {
              field: isCityLabeledAsSuburb
                ? t('global.field.suburb.label')
                : t('global.field.city.label'),
            }),
          }}
          required
        />
      </Grid>

      <Grid size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 6}>
        <TextFormControl
          name={`${prefix}${fieldNames?.postalCode ?? 'postalCode'}`}
          label={t('reservationCheckout.billingDetailsForm.postCode.label')}
          placeholder={t(
            'reservationCheckout.billingDetailsForm.postCode.placeholder',
          )}
          testIdPrefix={testIdPrefix}
          testId="postcode"
          textFieldProps={{
            slotProps: {
              htmlInput: { maxLength: 9 },
            },
            fullWidth: true,
          }}
          validationRules={{
            pattern: {
              value: POSTCODE_REGEX,
              message: t('global.field.zipcode.invalid.message'),
            },
            required: t('global.field.validation.fieldRequired.message', {
              field: t('reservationCheckout.billingDetailsForm.postCode.label'),
            }),
            minLength: {
              value: POSTAL_CODE_MIN_LENGTH,
              message: decodeHtmlEntities(
                t('global.field.validation.invalidLength.message', {
                  field: t(
                    'reservationCheckout.billingDetailsForm.postCode.label',
                  ),
                  minLength: POSTAL_CODE_MIN_LENGTH,
                  maxLength: POSTAL_CODE_MAX_LENGTH,
                }),
              ),
            },
          }}
          required
        />
      </Grid>
      <Grid size={pageType === 'profileEmea' ? { xs: 12, sm: 6 } : 6}>
        <TextFormControl
          name={`${prefix}country`}
          testIdPrefix={testIdPrefix}
          testId="country-select"
          select
          label={t('global.field.country.label')}
          defaultValue={pageConfig.country?.toUpperCase() ?? ''}
          selectData={countriesData}
          textFieldProps={{ fullWidth: true }}
          required
          disabled={disableCountry || pageType === 'profile'}
        />
      </Grid>
    </Grid>
  );
};
